import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'

const routes = [
  {
    path: '/',
    name: 'Layout',
    component: () => import('../views/layout/MainLayout.vue'),
    children: [
      { path: '', name: 'Home', component: () => import('../views/home/HomePage.vue') },
      { path: 'category/:id?', name: 'Category', component: () => import('../views/category/CategoryPage.vue') },
      { path: 'brands', name: 'BrandsList', component: () => import('../views/brand/BrandsList.vue') },
      { path: 'brand/:slug', name: 'BrandPage', component: () => import('../views/brand/BrandPage.vue') },
      { path: 'product/:id', name: 'ProductDetail', component: () => import('../views/product/ProductDetail.vue') },
      { path: 'cart', name: 'Cart', component: () => import('../views/cart/CartPage.vue') },
      { path: 'checkout', name: 'Checkout', component: () => import('../views/checkout/CheckoutPage.vue'), meta: { requiresAuth: true } },
      { path: 'user', name: 'UserCenter', component: () => import('../views/user/UserCenter.vue'), meta: { requiresAuth: true } },
      { path: 'user/chat', name: 'UserChat', component: () => import('../views/user/UserChatPage.vue'), meta: { requiresAuth: true } },
      { path: 'orders', name: 'Orders', component: () => import('../views/user/OrdersPage.vue'), meta: { requiresAuth: true } },
      { path: 'favorites', name: 'Favorites', component: () => import('../views/user/FavoritesPage.vue'), meta: { requiresAuth: true } },
      { path: 'coupons', name: 'Coupons', component: () => import('../views/user/CouponsPage.vue'), meta: { requiresAuth: true } },
      { path: 'footprint', name: 'Footprint', component: () => import('../views/user/FootprintPage.vue'), meta: { requiresAuth: true } },
      { path: 'points', name: 'PointsMall', component: () => import('../views/points/PointsMall.vue'), meta: { requiresAuth: true } },
      { path: 'checkin', name: 'Checkin', component: () => import('../views/checkin/Checkin.vue'), meta: { requiresAuth: true } },
      { path: 'about', name: 'About', component: () => import('../views/pages/AboutPage.vue') },
      { path: 'shipping', name: 'Shipping', component: () => import('../views/pages/ShippingPage.vue') },
      { path: 'returns', name: 'Returns', component: () => import('../views/pages/ReturnsPage.vue') },
      { path: 'privacy', name: 'Privacy', component: () => import('../views/pages/PrivacyPage.vue') },
      { path: 'contact', name: 'Contact', component: () => import('../views/pages/ContactPage.vue') },
      { path: 'search', name: 'Search', component: () => import('../views/search/SearchPage.vue') }
    ]
  },
  { path: '/login', name: 'Login', component: () => import('../views/auth/LoginPage.vue') },
  { path: '/register', name: 'Register', component: () => import('../views/auth/RegisterPage.vue') },
  { path: '/forgot-password', name: 'ForgotPassword', component: () => import('../views/auth/ForgotPasswordPage.vue') },
  { path: '/payment/mock', name: 'PaymentMock', component: () => import('../views/payment/PaymentMockPage.vue') },
  { path: '/payment/return', name: 'PaymentReturn', component: () => import('../views/payment/PaymentReturnPage.vue') },
  { path: '/debug', name: 'Debug', component: () => import('../views/DebugPage.vue') },
  { path: '/admin', name: 'AdminDashboard', component: () => import('../views/admin/AdminDashboard.vue'), meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/admin/orders', name: 'AdminOrders', component: () => import('../views/admin/AdminOrders.vue'), meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/admin/settings', name: 'AdminSettings', component: () => import('../views/admin/AdminSettings.vue'), meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/admin/chat', name: 'AdminChat', component: () => import('../views/admin/AdminChat.vue'), meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('../views/pages/NotFound.vue') }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// 检查登录状态的辅助函数
function checkLoginStatus() {
  // 1. 检查 Vuex store
  const storeToken = store.state.token

  // 2. 检查 localStorage
  const localToken = localStorage.getItem('token')

  // 3. 检查 sessionStorage（作为备选）
  const sessionToken = sessionStorage.getItem('token')
  
  // 清理并验证 localStorage token
  const cleanLocalToken = localToken ? localToken.trim().replace(/^"|"$/g, '') : ''
  const cleanSessionToken = sessionToken ? sessionToken.trim().replace(/^"|"$/g, '') : ''
  
  // 判断各个 token 是否有效
  const isStoreTokenValid = !!storeToken && storeToken !== 'null' && storeToken !== 'undefined'
  const isLocalTokenValid = !!cleanLocalToken && cleanLocalToken !== 'null' && cleanLocalToken !== 'undefined'
  const isSessionTokenValid = !!cleanSessionToken && cleanSessionToken !== 'null' && cleanSessionToken !== 'undefined'
  
  // 使用任何可用的有效 token
  const token = isStoreTokenValid ? storeToken : (isLocalTokenValid ? cleanLocalToken : cleanSessionToken)
  
  // 如果 Vuex 没有有效 token 但 localStorage 有，同步到 Vuex
  if (!isStoreTokenValid && isLocalTokenValid) {
    console.log('[Router] Syncing token from localStorage to Vuex')
    store.commit('SET_TOKEN', cleanLocalToken)
  }
  
  // 如果 Vuex 没有有效 token 但 sessionStorage 有，同步到 Vuex
  if (!isStoreTokenValid && !isLocalTokenValid && isSessionTokenValid) {
    console.log('[Router] Syncing token from sessionStorage to Vuex')
    store.commit('SET_TOKEN', cleanSessionToken)
  }
  
  const isLoggedIn = !!token && token !== 'null' && token !== 'undefined'
  console.log('[Router] Token sources - store:', isStoreTokenValid, 'local:', isLocalTokenValid, 'session:', isSessionTokenValid, 'result:', isLoggedIn)
  
  return isLoggedIn
}

router.beforeEach((to, from, next) => {
  console.log('[Router] Navigating to:', to.path)
  const isLoggedIn = checkLoginStatus()
  console.log('[Router] isLoggedIn:', isLoggedIn)
  
  if (to.meta.requiresAuth && !isLoggedIn) {
    console.log('[Router] Redirecting to login')
    next('/login?redirect=' + to.path)
    return
  }
  
  // 检查管理员权限
  if (to.meta.requiresAdmin) {
    const user = store.state.user
    const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin')
    if (!isAdmin) {
      console.log('[Router] Admin access denied')
      next('/')
      return
    }
  }
  
  next()
})

export default router
